import type { VorankuendigungData } from '../../types';

/**
 * Vertrag „Vorankündigung unterschreiben“ (docs/vorankuendigung-unterschrift-plan.md).
 *
 * Fassungen, Token und Unterschriften liegen in `vorank_fassungen`
 * (SQL Nr. 186/187), NIE im Projekt-Blob `report.vorankuendigung`: der
 * Autosave schreibt den ganzen Stand zurück und würde eine Unterschrift, die
 * eine RPC dort ablegt, wieder löschen.
 */

/** Zustand einer Fassung, wie ihn `vorank_unterschrift_public` meldet. */
export type VorankFassungStatus = 'offen' | 'unterschrieben' | 'ersetzt' | 'abgelaufen';

/** Wie die Unterschrift hereinkam: über den Link oder als eingelesene Datei. */
export type VorankUnterschriftWeg = 'digital' | 'papier';

/** Digitaler Weg: am Gerät gezeichnet (Finger/Maus) oder als Bild hochgeladen. */
export type VorankUnterschriftArt = 'gezeichnet' | 'bild';

/** Hash-Route der öffentlichen Seite: `#/vorankuendigung/<token>`. */
export const VORANK_ROUTE_PRAEFIX = '#/vorankuendigung/';

/** Der Token: 64 Hex-Zeichen, nur von `vorank_fassung_anlegen` erzeugt. */
export const VORANK_TOKEN_MUSTER = /^[0-9a-f]{64}$/;

/** Gültigkeit eines Links ab Anlage der Fassung. */
export const VORANK_LINK_TAGE = 90;

/** Obergrenze des Unterschriftsbilds (PNG als data-URL, gesamte Zeichenkette). */
export const VORANK_BILD_MAX_ZEICHEN = 200_000;
export const VORANK_BILD_PRAEFIX = 'data:image/png;base64,';

export const VORANK_NAME_MIN = 2;
export const VORANK_NAME_MAX = 120;
/**
 * Vor- und Nachname auf der öffentlichen Seite (E6, Betreiber 29.10.): je
 * höchstens 60 Zeichen. An Route und RPC geht weiter EIN Feld `name`
 * („Vorname Nachname“, zusammen ≤ `VORANK_NAME_MAX`).
 */
export const VORANK_NAMENSTEIL_MAX = 60;
export const VORANK_FUNKTION_MAX = 120;
/**
 * Eingetragenes Datum der Unterschrift (E6): Kalendertag `YYYY-MM-DD`, wie
 * ihn `<input type="date">` liefert und die RPC als `date` annimmt.
 */
export const VORANK_DATUM_MUSTER = /^\d{4}-\d{2}-\d{2}$/;
export const VORANK_KORREKTUR_MAX = 2000;
/** Korrekturmeldungen je Fassung, danach lehnt die RPC ab. */
export const VORANK_KORREKTUR_DECKEL = 5;

/** Eingabe der hochgeladenen Bilddatei auf der öffentlichen Seite. */
export const VORANK_BILD_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
/** Eingelesene Datei (Papierweg) im Bearbeiter-Dialog. */
export const VORANK_EINLESEN_MAX_BYTES = 25 * 1024 * 1024;

/** Vercel-Route, über die die öffentliche Seite unterschreibt oder Korrektur meldet. */
export const VORANK_API_PFAD = '/api/vorank-unterschrift';

/** Link für QR und Begleitblatt — Muster `freigabeLinkUrl` (GBU). */
export const vorankLinkUrl = (origin: string, pathname: string, token: string): string =>
    `${origin}${pathname || '/'}${VORANK_ROUTE_PRAEFIX}${token}`;

/** Unterschrift einer Fassung (digital: mit Bild; Papier: Bild null). */
export interface VorankUnterschrift {
    weg: VorankUnterschriftWeg;
    /** ISO-Zeitpunkt. */
    am: string;
    name: string | null;
    funktion: string | null;
    /** PNG-data-URL; nur digital. */
    bild: string | null;
    /** Nur digital. */
    art: VorankUnterschriftArt | null;
    /**
     * Kalendertag laut Eintrag des Unterzeichners (`YYYY-MM-DD`, E6) — kommt
     * ins Feld „Ort, Datum“ der PDF. Nur digital; null bei Papier und bei
     * Unterschriften von vor E6. Der echte Zeitpunkt bleibt `am`.
     */
    datum: string | null;
}

export interface VorankKorrektur {
    am: string;
    name: string | null;
    text: string;
}

/**
 * Eine Zeile aus `vorank_fassungen`, wie sie der Bearbeiter liest
 * (authenticated, RLS `is_admin() or can_access_project`).
 */
export interface VorankFassungZeile {
    id: string;
    projectId: string;
    inhalt: VorankuendigungData;
    inhaltHash: string;
    /** Seitenzahl der Vorankündigung OHNE Begleitblatt. */
    seiten: number;
    archivEintragId: string | null;
    token: string;
    gueltigBis: string;
    createdAt: string;
    createdBy: string | null;
    unterschrift: VorankUnterschrift | null;
    /** Original der eingelesenen Datei (project_documents.id). */
    scanDokumentId: string | null;
    /** Unterschriebene A4-PDF ohne Begleitblatt (project_documents.id) — Quelle für Plot und Behörde. */
    signiertDokumentId: string | null;
    /** Jüngste Korrekturmeldung (die Spalten halten nur die letzte). */
    korrektur: VorankKorrektur | null;
    ersetztAm: string | null;
    mailGesendetAm: string | null;
    mailFehler: string | null;
}

/** Abgeleiteter Zustand für die Statuskarte (reine Funktion in vorankUnterschriftService). */
export type VorankKartenStatus =
    | 'keine_fassung'
    | 'offen'
    | 'korrektur'
    | 'unterschrieben'
    | 'unterschrieben_veraltet';

/** Antwort von `vorank_fassung_anlegen`. */
export interface VorankFassungAngelegt {
    fassungId: string;
    token: string;
    gueltigBis: string;
    /** false = Fassung mit gleichem Hash existierte schon (gleicher Token). */
    neu: boolean;
    /** Die Fassung (gleicher Hash) ist schon unterschrieben — vom Server, nicht aus einer geladenen Liste. */
    unterschrieben: boolean;
    /** ISO-Zeitpunkt der Anlage der Fassung (`created_at`). */
    fassungVom: string;
}

/** Antwort von `vorank_unterschrift_public` (anon). null = Token unbekannt. */
export interface VorankPublicAntwort {
    status: VorankFassungStatus;
    projektName: string | null;
    projektNummer: string | null;
    fassungVom: string;
    gueltigBis: string;
    seiten: number;
    inhalt: VorankuendigungData;
    unterschrift: VorankUnterschrift | null;
    /** Nur ob und wann gemeldet wurde — der Text geht nicht an die Seite zurück. */
    korrekturGemeldetAm: string | null;
}

// ── Vercel-Route api/vorank-unterschrift.ts ─────────────────────────────

export type VorankRouteAnfrage =
    | {
        aktion: 'unterschreiben';
        token: string;
        /** „Vorname Nachname“ — die Seite setzt beide Felder getrimmt mit einem Leerzeichen zusammen. */
        name: string;
        funktion: string;
        /** PNG-data-URL, ≤ VORANK_BILD_MAX_ZEICHEN. */
        bild: string;
        art: VorankUnterschriftArt;
        /**
         * Eingetragenes Datum `YYYY-MM-DD` (E6): nicht in der Zukunft, nicht vor
         * dem Tag der Fassung (Europe/Berlin) — genau prüft die RPC.
         */
        datum: string;
    }
    | {
        aktion: 'korrektur';
        token: string;
        name: string;
        text: string;
    };

/**
 * 200 ok · 400 eingabe · 404 ungueltig · 409 status (ersetzt/abgelaufen, oder
 * Korrektur nach Unterschrift) · 429 zu_viele · 500 server.
 * `mail`: 'gesendet' | 'fehler' | 'keine' (kein Empfänger oder `bereits`).
 */
export type VorankRouteAntwort =
    | { ok: true; bereits: boolean; mail: 'gesendet' | 'fehler' | 'keine' }
    | { ok: false; fehler: 'eingabe' | 'ungueltig' | 'zu_viele' | 'server'; text?: string }
    | { ok: false; fehler: 'status'; status: VorankFassungStatus | 'unterschrieben'; text?: string };

// ── PDF ─────────────────────────────────────────────────────────────────

/** Zweiter Parameter von `generateVorankuendigungPDF` — nur für die unterschriebene Fassung. */
export interface VorankPdfUnterschrift {
    /** PNG-data-URL. */
    bild: string;
    name: string;
    funktion?: string | null;
    /** ISO-Zeitpunkt der Unterschrift — die graue Nachweiszeile nennt ihn. */
    am: string;
    /**
     * Eingetragenes Datum `YYYY-MM-DD` (E6) für „Ort, Datum“; fehlt es, gilt
     * der Kalendertag von `am`.
     */
    datum?: string | null;
    art: VorankUnterschriftArt;
    /** Letzte 6 Zeichen des Tokens, für die graue Nachweiszeile. */
    kennung: string;
    /** ISO-Zeitpunkt der Fassung. */
    fassungVom: string;
}

export interface VorankBegleitblattEingabe {
    /** null: kein Link (SQL fehlt, Zeitbudget überschritten) → Erläuterungen + Papierweg, kein QR-Kasten. */
    link: { url: string; gueltigBis: string } | null;
    data: VorankuendigungData;
    /** Rücksendeadresse für den Papierweg (Mail des Bearbeiters), optional. */
    kontaktMail?: string | null;
    /** ISO-Zeitpunkt der Fassung (Kopfzeile „Fassung vom …“). */
    fassungVom: string;
}

// ── Papierweg: Datei einlesen ───────────────────────────────────────────

export type VorankDrehung = 0 | 90 | 180 | 270;

/** Eine Seite aus den gewählten Dateien, für Vorschau und Auswahl. */
export interface VorankEinleseSeite {
    /** Index in der übergebenen Dateiliste. */
    dateiIndex: number;
    /** Seite in der Datei (Bild: immer 0). */
    seitenIndex: number;
    breiteMm: number;
    hoeheMm: number;
    /** PDF-Seite mit /Annots (Adobe Fill & Sign, Signaturfeld …) — wird gerastert. */
    hatAnnotationen: boolean;
    /** Vorgeschlagene Drehung (quer → 90). */
    drehungVorschlag: VorankDrehung;
    /** Kleines Vorschaubild (data-URL, JPEG). */
    vorschau: string;
}

export interface VorankEinleseAuswahl {
    seiten: { dateiIndex: number; seitenIndex: number; drehung: VorankDrehung }[];
}
